/**
 * MoveBlockModal — re-home a saved block: change its PCO and/or its work date.
 * The new date decides the week (shown live). Allowed only into open weeks; a
 * week already sent to Hoffman must be reopened first (the modal blocks it
 * client-side; the server is authoritative). Supersedes the reassign picker.
 *
 * Presented as "Change work date" (client, 2026-07-28). The destination-PCO
 * picker and everything behind it are unchanged and still work — the labels
 * narrowed to the job this dialog actually does day to day, because the other
 * two reasons to reach for it already have better-named homes: Transfer Block
 * for submission placement, and Re-open + re-save for a wrong PCO on unsent
 * work. Only strings moved here; no behavior did.
 */
import { useEffect, useMemo, useState } from 'react';
import type { RollupBlockSummary, RollupListItem, SettingsPco } from '@coffman-tm/shared';
import { api, ApiCallError } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { formatDateShort, formatHours, formatPcoNumber, gcName, weekEndingFor } from '../lib/format';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';
import { Dropdown } from './design';

const REASON_MIN = 10;
const REASON_MAX = 280;

export interface MoveBlockModalProps {
  open: boolean;
  block: RollupBlockSummary | null;
  sourcePcoNumber: string;
  onCancel: () => void;
  onMoved: () => void;
}

export function MoveBlockModal({ open, block, sourcePcoNumber, onCancel, onMoved }: MoveBlockModalProps) {
  const { token, me } = useAuth();
  const [pcos, setPcos] = useState<SettingsPco[]>([]);
  const [rollups, setRollups] = useState<RollupListItem[]>([]);
  const [destPcoId, setDestPcoId] = useState('');
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !token || !block) return;
    setDestPcoId(block.current_pco_id ?? '');
    setDate(block.report_date);
    setReason('');
    setErr(null);
    void (async () => {
      try {
        const [pcoList, rollupList] = await Promise.all([api.settings.pcos(token), api.rollups.list(token)]);
        setPcos(pcoList);
        setRollups(rollupList.rollups);
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'Failed to load PCOs');
      }
    })();
  }, [open, token, block]);

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  // PCO options: real PCOs, not void. Includes the current PCO (same-PCO moves).
  const pcoOptions = useMemo(
    () => pcos.filter((p) => p.status !== 'void').sort((a, b) => a.pco_number.localeCompare(b.pco_number)),
    [pcos],
  );
  const destPcoOptions = useMemo(
    () => [
      { value: '', label: 'Select a PCO…' },
      ...pcoOptions.map((p) => ({ value: p.id, label: `PCO ${formatPcoNumber(p.pco_number)} — ${p.description}` })),
    ],
    [pcoOptions],
  );

  // The shared helper returns null for a blank/unparseable date, so the modal
  // never claims a destination week until the input is a real calendar date.
  const targetWeek = useMemo(() => weekEndingFor(date), [date]);

  // Is the destination (PCO, week) already sent? Then it must be reopened first.
  const destSent = useMemo(() => {
    if (!destPcoId || !targetWeek) return false;
    const r = rollups.find((x) => x.pco.id === destPcoId && x.week_ending_date === targetWeek);
    return !!r && r.status !== 'in_progress';
  }, [rollups, destPcoId, targetWeek]);

  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= REASON_MIN && trimmedReason.length <= REASON_MAX;
  const changed = !!block && (destPcoId !== (block.current_pco_id ?? '') || date !== block.report_date);
  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(date);
  const canSubmit = destPcoId !== '' && dateValid && changed && reasonValid && !destSent && !busy;

  if (!open || !block) return null;

  const handleConfirm = async () => {
    if (!token || !canSubmit) return;
    setBusy(true);
    setErr(null);
    try {
      await api.blocks.move(token, block.daily_entry_id, {
        destination_pco_id: destPcoId,
        report_date: date,
        reason: trimmedReason,
      });
      onMoved();
    } catch (e) {
      if (e instanceof ApiCallError && e.payload && typeof e.payload === 'object') {
        const p = e.payload as { message?: string; error?: string };
        setErr(p.message ?? p.error ?? 'Move failed');
      } else setErr(e instanceof Error ? e.message : 'Move failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="pdf-modal-overlay" onClick={() => { if (!busy) onCancel(); }} role="dialog" aria-modal="true" aria-label="Change work date">
      <div className="confirm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="confirm-modal-head"><div className="confirm-modal-title">Change work date</div></div>
        <div className="confirm-modal-body">
          <div style={{ background: 'var(--surface-warm, var(--surface))', border: '1px solid var(--rule)', borderRadius: 4, padding: '8px 10px', fontSize: 13, marginBottom: 12 }}>
            <div style={{ color: 'var(--ink-3)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Currently on PCO {formatPcoNumber(sourcePcoNumber)}
            </div>
            <div style={{ marginTop: 2 }}>
              <span className="mono">{block.activity_code}</span> · work date {formatDateShort(block.report_date)}
            </div>
            <div className="muted" style={{ marginTop: 2 }}>
              {formatHours(block.labor_hours)} labor hrs · {formatHours(block.equipment_hours)} equip hrs
            </div>
          </div>

          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Destination PCO</label>
          <Dropdown options={destPcoOptions} value={destPcoId} onChange={setDestPcoId} disabled={busy} style={{ width: '100%' }} />

          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, margin: '12px 0 4px' }}>New work date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} disabled={busy} style={selectStyle} />
          {targetWeek && (
            <p className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>→ week ending {formatDateShort(targetWeek)}</p>
          )}
          {destSent && (
            <p style={{ fontSize: 11.5, marginTop: 4, color: 'var(--danger)' }}>
              That week was already sent to {gcName(me)}. Reopen it first, then move work into it.
            </p>
          )}

          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, margin: '12px 0 4px' }}>
            Reason <span className="muted" style={{ fontWeight: 400 }}>(required)</span>
          </label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} rows={3}
            placeholder="Why is this block moving? (10–280 chars)" style={{ ...selectStyle, resize: 'vertical', fontFamily: 'inherit' }} />
          <div className="muted" style={{ fontSize: 11, marginTop: 2, textAlign: 'right' }}>{trimmedReason.length}/{REASON_MAX}</div>

          {err && <div style={{ color: 'var(--danger)', fontSize: 12.5, marginTop: 8 }}>{err}</div>}
        </div>
        <div className="confirm-modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-cta" onClick={handleConfirm} disabled={!canSubmit}>
            {busy ? 'Saving…' : 'Confirm date'}
          </button>
        </div>
      </div>
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  width: '100%', padding: '7px 9px', fontSize: 13,
  border: '1px solid var(--rule-strong, var(--rule))', borderRadius: 4,
  background: 'var(--surface)', color: 'var(--ink)',
};
